import React, { useCallback, useEffect, useState } from "react";
import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../components/mobileUi";
import { CenterSpinner, M, Png, PropCell } from "../../components/managementUi";
import {
  DesktopBack,
  DesktopButton,
  DesktopPage,
  DetailItem,
  DetailSection,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { EMP_DEPARTMENT } from "../../utils/managerRole";
import { getManagerLeaveDetailApi, approveLeaveApi } from "../../api/leaveManager.api";
import { REQUEST_STATUS } from "../../config";
import { leaveStatusUi } from "./LeaveApprovalList";

const fmtDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

// Header khớp RequestLeaveManagerInfoHeader của app.
const ManagerInfoHeader = ({ detail }) => {
  const ui = leaveStatusUi(detail?.status);
  const icon =
    detail?.status === REQUEST_STATUS.APPROVED
      ? appImages.icon_check_circle
      : detail?.status === REQUEST_STATUS.REJECTED
      ? appImages.icon_reject_circle
      : null;
  return (
    <Box pt="16px">
      <Flex align="center" flexWrap="wrap" gap="4px">
        <Text fontSize="16px" fontWeight={600} color={M.baseContent}>
          {detail?.employeeObject?.fullName || "-"}
        </Text>
        <Text fontSize="16px" fontWeight={600} color={M.baseContent}>
          ({detail?.employeeObject?.code || "-"})
        </Text>
      </Flex>
      <Flex align="center" flexWrap="wrap" gap="8px" mt="4px">
        <Text fontSize="14px" fontWeight={600} color={M.tertiary}>
          {detail?.createdAt ? moment(detail.createdAt).format("DD/MM/YYYY") : "--/--/----"}
        </Text>
        <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
          -
        </Text>
        <Flex align="center" bg={ui.bg} borderRadius="6px" px="8px" py="3px" gap="8px">
          {icon ? <Png src={icon} size={18} /> : null}
          <Text fontSize="14px" fontWeight={600} color={ui.color}>
            {ui.label}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
};

const LeaveApprovalDetail = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { id } = useParams();
  const toast = useToast();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getManagerLeaveDetailApi(id)
      .then((d) => setDetail(d || null))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [id]);

  const doApprove = useCallback(
    async (status) => {
      setSubmitting(true);
      try {
        await approveLeaveApi(id, status);
        toast({ title: "Duyệt Thành công", status: "success", duration: 2500, position: "top" });
        navigate(-1);
      } catch (e) {
        toast({ title: e?.message || "Duyệt thất bại", status: "error", duration: 3000, position: "top" });
      } finally {
        setSubmitting(false);
      }
    },
    [id, navigate, toast]
  );

  const emp = detail?.employeeObject || {};

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const ui = leaveStatusUi(detail?.status);
    return (
      <DesktopPage
        title="Chi tiết đơn xin nghỉ phép"
        right={<DesktopBack onClick={() => navigate(-1)} />}
      >
        {loading ? (
          <CenterSpinner />
        ) : !detail ? (
          <Text fontSize="14px" color={M.subTitle} textAlign="center" pt="40px">
            Không tìm thấy thông tin đơn xin nghỉ phép
          </Text>
        ) : (
          <DetailSection
            title={`${emp?.fullName || "-"} (${emp?.code || "-"})`}
            right={
              <Flex align="center" gap="12px">
                <Flex align="center" bg={ui.bg} borderRadius="6px" px="10px" py="4px">
                  <Text fontSize="14px" fontWeight={600} color={ui.color}>
                    {ui.label}
                  </Text>
                </Flex>
                {detail?.status === REQUEST_STATUS.PENDING && (
                  <>
                    <DesktopButton
                      isDisabled={submitting}
                      onClick={() => !submitting && doApprove(REQUEST_STATUS.APPROVED)}
                    >
                      Phê duyệt
                    </DesktopButton>
                    <DesktopButton
                      variant="danger"
                      isDisabled={submitting}
                      onClick={() => !submitting && doApprove(REQUEST_STATUS.REJECTED)}
                    >
                      Từ chối
                    </DesktopButton>
                  </>
                )}
              </Flex>
            }
          >
            <DetailItem
              label="Ngày tạo"
              value={detail?.createdAt ? moment(detail.createdAt).format("DD/MM/YYYY") : "-"}
            />
            {emp?.department === EMP_DEPARTMENT.BUSINESS && (
              <>
                <DetailItem label="Khối" value={emp?.saleBlock?.name} />
                <DetailItem label="Vùng" value={emp?.saleArea?.name} />
              </>
            )}
            {emp?.department === EMP_DEPARTMENT.SUPPORT_BUSINESS && (
              <DetailItem label="Phòng" value={emp?.supportDepartment?.name} />
            )}
            <DetailItem label="Ngày yêu cầu" value={fmtDate(detail?.requestDate)} />
            <DetailItem label="Ngày bắt đầu" value={fmtDate(detail?.startDate)} />
            <DetailItem label="Số ngày nghỉ" value={detail?.daysRequested ?? 0} />
            <DetailItem label="Loại nghỉ phép" value={detail?.leaveType?.name} />
            <DetailItem label="Lý do" value={detail?.reason} span={3} />
          </DetailSection>
        )}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb={detail?.status === REQUEST_STATUS.PENDING ? "100px" : "40px"}>
      <AppBar title="Chi tiết đơn đăng ký đơn xin nghỉ phép" />

      {loading ? (
        <CenterSpinner />
      ) : !detail ? (
        <Text fontSize="14px" color={M.subTitle} textAlign="center" pt="40px">
          Không tìm thấy thông tin đơn xin nghỉ phép
        </Text>
      ) : (
        <>
          <Box px="16px">
            <ManagerInfoHeader detail={detail} />

            {emp?.department === EMP_DEPARTMENT.BUSINESS && (
              <>
                <PropCell label="Khối" value={emp?.saleBlock?.name} />
                <PropCell label="Vùng" value={emp?.saleArea?.name} />
              </>
            )}
            {emp?.department === EMP_DEPARTMENT.SUPPORT_BUSINESS && (
              <PropCell label="Phòng" value={emp?.supportDepartment?.name} />
            )}
            <PropCell label="Ngày yêu cầu" value={fmtDate(detail?.requestDate)} />
            <PropCell label="Ngày bắt đầu" value={fmtDate(detail?.startDate)} />
            <PropCell label="Số ngày nghỉ" value={detail?.daysRequested ?? 0} />
            <PropCell label="Lý do" value={detail?.reason} borderBottom={false} />
            <PropCell label="Loại nghỉ phép" value={detail?.leaveType?.name} borderBottom={false} />
          </Box>

          {/* Thanh hành động cố định đáy màn - khớp app */}
          {detail?.status === REQUEST_STATUS.PENDING && (
            <Flex
              position="fixed"
              bottom="0"
              left="50%"
              transform="translateX(-50%)"
              w="100%"
              maxW="480px"
              bg="white"
              zIndex={20}
              justify="space-evenly"
              align="center"
              p="16px"
              gap="12px"
            >
              <Flex
                as="button"
                type="button"
                flex="1"
                maxW="170px"
                h="48px"
                align="center"
                justify="center"
                gap="8px"
                borderRadius="8px"
                border="1px solid"
                borderColor={M.border}
                opacity={submitting ? 0.6 : 1}
                onClick={() => !submitting && doApprove(REQUEST_STATUS.APPROVED)}
              >
                <Png src={appImages.icon_check_circle} size={24} />
                <Text fontSize="16px" fontWeight={600} color={M.baseContent}>
                  Phê duyệt
                </Text>
              </Flex>
              <Flex
                as="button"
                type="button"
                flex="1"
                maxW="170px"
                h="48px"
                align="center"
                justify="center"
                gap="8px"
                borderRadius="8px"
                border="1px solid"
                borderColor={M.border}
                opacity={submitting ? 0.6 : 1}
                onClick={() => !submitting && doApprove(REQUEST_STATUS.REJECTED)}
              >
                <Png src={appImages.icon_reject_circle} size={24} />
                <Text fontSize="16px" fontWeight={600} color={M.baseContent}>
                  Từ chối
                </Text>
              </Flex>
            </Flex>
          )}
        </>
      )}
    </Box>
  );
};

export default LeaveApprovalDetail;
